import { useCallback, useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';
import apiClient from '../api/client';
import { useToast } from '../components/ui/Toast';
import { usePermission, useAnyPermission } from '../auth/usePermission';
import { Permission } from '../auth/permissions';
import { checkboxClass } from '../components/ui/Input';
import { formatEUR } from '../lib/format';
import { Modal } from '../components/receiving/Modal';

/**
 * WP-4 — Supplier order registry (PO_CREATE / PO_SEND / PO_SHIPPED / PO_RECEIVE / PO_CANCEL).
 *
 * The screen the flow needed and did not have: a purchase order fired automatically at
 * ORDER_CONFIRM can fail to reach the supplier (API down), and until now nothing surfaced that.
 * A PO left PENDING is *ordered but not transmitted* — it retries with backoff and escalates to
 * OWNER/MANAGER past poRetryMax, while the customer order stays CONFIRMED with its line reading
 * "awaiting supplier". That state, the manual re-send, the approval of a held PO and the
 * cancel-request outcome all live here.
 *
 * INV-32 / E5 — a PO above the automatic-sending threshold (autoApprovalThresholdHT, €500 excl.
 * VAT) is PENDING_APPROVAL: nothing has reached the supplier. Approving it and sending it are
 * two separate acts, and two separate buttons: Approve (PO_APPROVE) leaves the PO PENDING —
 * "approved, still to transmit" — and Transmit (PO_PLACE) is what actually places it.
 *
 * Backed by GET /purchasing/orders(?all=1), GET /purchasing/orders/:id and
 * POST /purchasing/orders/:id/{send,approve,cancel}. Goods-in stays on the Reception screen.
 */

/** 3.12 — a customer line on no supplier order (GET /purchasing/unsourced-lines). */
interface UnsourcedLine {
  lineId: string;
  orderId: string;
  orderNumber: string;
  customerName: string | null;
  productId: string;
  purchasePriceHT: string | number | null;
  sku: string | null;
  name: string | null;
  brand: string | null;
  manufacturerRef: string | null;
  quantity: number;
  since: string;
}
interface SupplierPick {
  id: string;
  code: string;
  name: string;
  apiEnabled: boolean;
}

interface PoSummary {
  id: string;
  poNumber: string;
  status: string;
  supplierName: string;
  supplierApiEnabled: boolean;
  supplierOrderRef: string | null;
  orderId: string | null;
  orderNumber: string | null;
  totalHT: string | number;
  lineCount: number;
  qtyOrdered: number;
  qtyReceived: number;
  createdAt: string;
  autoCreated: boolean;
  /** Set once a PO held above the threshold has been cleared (INV-32). Sending is still a step. */
  approvedAt: string | null;
  sendAttempts: number;
  lastSendError: string | null;
  nextRetryAt: string | null;
  escalatedAt: string | null;
  closedShort: boolean;
  brfNumber: string | null;
}

interface PoLine {
  id: string;
  productId: string;
  quantity: number;
  quantityCustomer: number | null;
  quantityReceived: number;
  quantityShort: number;
  received: boolean;
  customerReleased: boolean;
  orderLineId: string | null;
  productSku: string | null;
  productName: string | null;
  productBrand: string | null;
}

interface PoDetail extends Omit<
  PoSummary,
  'lineCount' | 'qtyOrdered' | 'qtyReceived' | 'createdAt'
> {
  lines: PoLine[];
}

/** POST /approve — it approves ONLY: `sent` is always false, transmission is a second call. */
interface ApproveResponse {
  approved: boolean;
  status: string;
  reason?: string;
  error?: string;
}

interface SendResponse {
  sent: boolean;
  status: string;
  supplierOrderRef?: string;
  reason?: string;
  error?: string;
  attempts?: number;
  escalated?: boolean;
  nextRetryAt?: string;
  dueAt?: string;
}

interface CancelResponse {
  status: string;
  supplierCancelled: boolean;
  tooLate: boolean;
  reason?: string;
}

const STATUS_TONE: Record<string, string> = {
  PENDING: 'bg-warning/15 text-warning',
  PENDING_APPROVAL: 'bg-warning/15 text-warning',
  CONFIRMED: 'bg-success/15 text-success',
  SHIPPED: 'bg-primary/15 text-primary',
  RECEIVED: 'bg-muted text-muted-foreground',
  CANCELLED: 'bg-muted text-muted-foreground',
};

import i18n from '../i18n';
import { enumLabel } from '../lib/enumLabel';
import { PageHeader } from '../components/ui/PageHeader';

const money = (v: string | number): string => formatEUR(v);

/** `last_send_error` prefix of a send that may have reached the supplier (INV-48, backend). */
const UNCERTAIN_SEND = 'UNCERTAIN_SEND';
const isUncertain = (p: Pick<PoSummary, 'status' | 'lastSendError'>) =>
  // PENDING_APPROVAL counts too: rows parked before approval and sending were split may already
  // sit at the supplier, and a park must never be invisible whatever status carries it.
  (p.status === 'PENDING' || p.status === 'PENDING_APPROVAL') &&
  !!p.lastSendError?.startsWith(UNCERTAIN_SEND);

const time = (iso: string | null | undefined) =>
  iso ? new Date(iso).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' }) : '—';

/**
 * Why a PO has not reached its supplier. `MANUAL_CHANNEL` is not a failure: that supplier has no
 * API, so its order is placed by hand at the daily cutoff (supplierCutoff applies to non-API
 * suppliers only). Everything else is a transmission failure being retried.
 */
function sendState(
  p: Pick<
    PoSummary,
    'status' | 'sendAttempts' | 'lastSendError' | 'nextRetryAt' | 'escalatedAt' | 'approvedAt'
  >
) {
  if (p.status !== 'PENDING' && p.status !== 'PENDING_APPROVAL') return null;
  if (isUncertain(p)) {
    return {
      tone: 'text-destructive font-semibold',
      label: i18n.t('supplierOrders.send_state.uncertain'),
      detail: i18n.t('supplierOrders.send_state.uncertainDetail'),
    };
  }
  if (p.status === 'PENDING_APPROVAL') {
    // Above the threshold: the order exists, and NOTHING has been sent to the supplier.
    return {
      tone: 'text-warning font-semibold',
      label: i18n.t('supplierOrders.send_state.awaitingApproval'),
      detail: i18n.t('supplierOrders.send_state.awaitingApprovalDetail'),
    };
  }
  if (p.lastSendError === 'MANUAL_CHANNEL') {
    return {
      tone: 'text-muted-foreground',
      label: i18n.t('supplierOrders.send_state.manual'),
      detail: i18n.t('supplierOrders.send_state.manualDetail', { when: time(p.nextRetryAt) }),
    };
  }
  if (p.escalatedAt) {
    return {
      tone: 'text-destructive font-semibold',
      label: i18n.t('supplierOrders.send_state.escalated', { attempts: p.sendAttempts }),
      detail: p.lastSendError ?? '',
    };
  }
  if (p.sendAttempts > 0) {
    return {
      tone: 'text-warning',
      label: i18n.t('supplierOrders.send_state.retrying', {
        attempts: p.sendAttempts,
        when: time(p.nextRetryAt),
      }),
      detail: p.lastSendError ?? '',
    };
  }
  if (p.approvedAt) {
    // Cleared, but sending is the second step — say so, or an approved PO reads like a stuck one.
    return {
      tone: 'text-primary',
      label: i18n.t('supplierOrders.send_state.approved'),
      detail: i18n.t('supplierOrders.send_state.approvedDetail', { when: time(p.approvedAt) }),
    };
  }
  return {
    tone: 'text-muted-foreground',
    label: i18n.t('supplierOrders.send_state.pending'),
    detail: '',
  };
}

/**
 * The badge label. `domain.poStatus` has no PENDING_APPROVAL entry (it would render as the raw
 * code), and that is the one status this screen exists to explain — so it is named here.
 */
const statusLabel = (status: string): string =>
  status === 'PENDING_APPROVAL'
    ? i18n.t('supplierOrders.statusPendingApproval')
    : enumLabel('poStatus', status);

export default function SupplierOrders() {
  const { t } = useTranslation();
  const { showApiError, push } = useToast();
  const canPlace = usePermission(Permission.PO_PLACE);
  // Clearing a held PO is PO_APPROVE (MANAGER/OWNER) and nothing else: PO_PLACE used to be
  // accepted here, which painted an Approve button the route answers 403 to — and WP-9 refuses
  // PO_CREATE + PO_APPROVE on one head precisely so the buyer does not clear their own spend.
  const canApprove = usePermission(Permission.PO_APPROVE);
  const canCancel = useAnyPermission(Permission.PO_PLACE, Permission.PO_APPROVE);
  const [rows, setRows] = useState<PoSummary[]>([]);
  const [detail, setDetail] = useState<PoDetail | null>(null);
  const [all, setAll] = useState(false);
  const [onlyBlocked, setOnlyBlocked] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);

  /** 3.12 — customer lines the automatic sourcing could not put on any supplier order. */
  const [unsourced, setUnsourced] = useState<UnsourcedLine[]>([]);
  const [suppliers, setSuppliers] = useState<SupplierPick[]>([]);
  /** Per unsourced line: the supplier chosen and the cost typed for the hand-raised PO. */
  const [handPo, setHandPo] = useState<Record<string, { supplierId: string; cost: string }>>({});
  const load = useCallback(async () => {
    try {
      const [{ data }, u, s] = await Promise.all([
        apiClient.get<PoSummary[]>(`/purchasing/orders${all ? '?all=1' : ''}`),
        apiClient
          .get<UnsourcedLine[]>('/purchasing/unsourced-lines')
          .catch(() => ({ data: [] as UnsourcedLine[] })),
        apiClient
          .get<SupplierPick[]>('/purchasing/suppliers')
          .catch(() => ({ data: [] as SupplierPick[] })),
      ]);
      setRows(data);
      setUnsourced(u.data);
      setSuppliers(s.data);
    } catch (err) {
      showApiError(err);
    }
  }, [all, showApiError]);

  /* 3.12 — raise the supplier order by hand for one customer line, linked to it, and send it
     at once (API supplier: transmitted; off-API: parked "to be ordered on the portal", then
     "Mark as placed"). The line stops reading "not ordered" the moment the PO exists. */
  const raiseByHand = async (u: UnsourcedLine) => {
    const pick = handPo[u.lineId] ?? {
      supplierId: suppliers[0]?.id ?? '',
      cost: String(u.purchasePriceHT ?? ''),
    };
    const cost = Number(pick.cost);
    if (!pick.supplierId || !Number.isFinite(cost) || cost < 0) {
      push({ tone: 'warning', message: t('supplierOrders.unsourced.needSupplierAndCost') });
      return;
    }
    setBusy(u.lineId);
    try {
      const { data: po } = await apiClient.post<{ id: string; poNumber: string }>(
        '/purchasing/orders',
        {
          supplierId: pick.supplierId,
          orderId: u.orderId,
          lines: [
            {
              productId: u.productId,
              orderLineId: u.lineId,
              quantity: u.quantity,
              unitPriceHT: cost,
            },
          ],
        },
        { headers: { 'X-Command-Id': crypto.randomUUID() } }
      );
      await apiClient
        .post(
          `/purchasing/orders/${po.id}/send`,
          {},
          { headers: { 'X-Command-Id': crypto.randomUUID() } }
        )
        .catch(() => undefined);
      push({
        tone: 'success',
        message: t('supplierOrders.unsourced.raised', { po: po.poNumber, order: u.orderNumber }),
      });
      await load();
    } catch (err) {
      showApiError(err);
    } finally {
      setBusy(null);
    }
  };

  useEffect(() => {
    void load();
  }, [load]);

  const openDetail = async (id: string) => {
    try {
      const { data } = await apiClient.get<PoDetail>(`/purchasing/orders/${id}`);
      setDetail(data);
    } catch (err) {
      showApiError(err);
    }
  };

  // Deep link from the purchases register: /purchasing/orders?po=<id> opens that PO.
  const [params] = useSearchParams();
  const linkedPo = params.get('po');
  useEffect(() => {
    if (linkedPo) void openDetail(linkedPo);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linkedPo]);

  const refresh = async (id: string) => {
    await load();
    if (detail?.id === id) await openDetail(id);
  };

  /** PO_SEND — transmit, or re-transmit after a failure. Never throws on a supplier outage: the
   *  response tells us whether it went out, so report the real outcome rather than a fake success. */
  const send = async (po: PoSummary | PoDetail, verifiedNotPlaced = false) => {
    if (
      verifiedNotPlaced &&
      !window.confirm(
        t('supplierOrders.confirmNotPlaced', { po: po.poNumber, supplier: po.supplierName })
      )
    )
      return;
    setBusy(po.id);
    try {
      const { data } = await apiClient.post<SendResponse>(
        `/purchasing/orders/${po.id}/send`,
        verifiedNotPlaced ? { verifiedNotPlaced: true } : {},
        { headers: { 'X-Command-Id': crypto.randomUUID() } }
      );
      if (data.sent) {
        push({
          tone: 'success',
          message: t('supplierOrders.toast.sent', {
            po: po.poNumber,
            ref: data.supplierOrderRef,
          }),
        });
      } else if (data.reason === 'MANUAL_CHANNEL') {
        push({
          tone: 'warning',
          message: t('supplierOrders.toast.manual', {
            po: po.poNumber,
            when: time(data.dueAt),
          }),
        });
      } else if (data.reason === 'UNCERTAIN_SEND') {
        push({
          tone: 'error',
          message: t('supplierOrders.toast.uncertain', {
            po: po.poNumber,
            supplier: po.supplierName,
          }),
        });
      } else if (data.reason === 'ALREADY_RECEIVED') {
        push({
          tone: 'warning',
          message: t('supplierOrders.toast.alreadyReceived', { po: po.poNumber }),
        });
      } else if (data.reason === 'NOT_PENDING') {
        push({
          tone: 'warning',
          message: t('supplierOrders.toast.notPending', {
            po: po.poNumber,
            status: statusLabel(data.status),
          }),
        });
      } else {
        push({
          tone: 'error',
          message: t('supplierOrders.toast.failed', {
            po: po.poNumber,
            error: data.error ?? t('supplierOrders.toast.supplierError'),
            attempts: data.attempts,
            when: time(data.nextRetryAt),
            escalated: data.escalated ? t('supplierOrders.toast.escalatedSuffix') : '',
          }),
        });
      }
      await refresh(po.id);
    } catch (err) {
      showApiError(err);
    } finally {
      setBusy(null);
    }
  };

  /**
   * Approve a PO held above autoApprovalThresholdHT (INV-32). It clears the spend and NOTHING
   * else: the PO comes back PENDING — "approved, still to transmit" — and the Transmit button
   * that then appears on the row is the second, deliberate step (E5).
   */
  const approve = async (po: PoSummary | PoDetail) => {
    setBusy(po.id);
    try {
      const { data } = await apiClient.post<ApproveResponse>(
        `/purchasing/orders/${po.id}/approve`,
        {},
        { headers: { 'X-Command-Id': crypto.randomUUID() } }
      );
      push({
        tone: data.approved ? 'success' : 'warning',
        message: data.approved
          ? t('supplierOrders.toast.approved', { po: po.poNumber })
          : data.reason === 'UNCERTAIN_SEND'
            ? t('supplierOrders.toast.approveUncertain', {
                po: po.poNumber,
                supplier: po.supplierName,
              })
            : t('supplierOrders.toast.approveNotPending', {
                po: po.poNumber,
                status: statusLabel(data.status),
              }),
      });
      await refresh(po.id);
    } catch (err) {
      showApiError(err); // 403 PO_SELF_APPROVAL: un acheteur n'approuve pas sa propre commande
    } finally {
      setBusy(null);
    }
  };

  /**
   * Off-API supplier: record the reference the purchaser got from the supplier's own portal and
   * take the PO PENDING -> CONFIRMED. This is the manual counterpart of send() for a supplier
   * that exposes no ordering endpoint (Inter Cars), and the only way such a PO ever leaves the
   * pending queue — without it the row sits there for good, reading like a transmission failure.
   */
  const markPlaced = async (po: PoSummary | PoDetail) => {
    const supplierOrderRef = window.prompt(
      t('supplierOrders.markPlacedPrompt', { po: po.poNumber, supplier: po.supplierName })
    );
    if (supplierOrderRef === null) return; // cancelled the dialog
    const ref = supplierOrderRef.trim();
    if (!ref) {
      push({ tone: 'warning', message: t('supplierOrders.toast.refRequired') });
      return;
    }
    setBusy(po.id);
    try {
      await apiClient.post(
        `/purchasing/orders/${po.id}/confirm`,
        { supplierOrderRef: ref },
        { headers: { 'X-Command-Id': crypto.randomUUID() } }
      );
      push({
        tone: 'success',
        message: t('supplierOrders.toast.markedPlaced', { po: po.poNumber, ref }),
      });
      await refresh(po.id);
    } catch (err) {
      showApiError(err);
    } finally {
      setBusy(null);
    }
  };

  /** PO_CANCEL — a refusal ("trop tard") is a normal outcome, not an error. */
  const cancel = async (po: PoSummary | PoDetail) => {
    const uncertain = isUncertain(po);
    const question = uncertain
      ? t('supplierOrders.confirmCancelUncertain', { po: po.poNumber, supplier: po.supplierName })
      : t('supplierOrders.toast.confirmCancel', { po: po.poNumber });
    if (!window.confirm(question)) return;
    setBusy(po.id);
    try {
      const { data } = await apiClient.post<CancelResponse>(
        `/purchasing/orders/${po.id}/cancel`,
        uncertain ? { verifiedNotPlaced: true } : {},
        { headers: { 'X-Command-Id': crypto.randomUUID() } }
      );
      push({
        tone: data.tooLate ? 'warning' : 'success',
        message: data.tooLate
          ? t('supplierOrders.toast.cancelRefused', {
              po: po.poNumber,
              reason: data.reason ?? t('supplierOrders.toast.tooLate'),
            })
          : t('supplierOrders.toast.cancelled', { po: po.poNumber }),
      });
      await refresh(po.id);
    } catch (err) {
      showApiError(err);
    } finally {
      setBusy(null);
    }
  };

  // "Not at the supplier and not moving on its own": a failed/escalated dispatch, and now also a
  // PO held above the threshold — it needs a human exactly as much, and nothing retries it.
  const isBlocked = (r: PoSummary) =>
    r.status === 'PENDING_APPROVAL' ||
    (r.status === 'PENDING' && (r.sendAttempts > 0 || !!r.escalatedAt));
  const visible = onlyBlocked ? rows.filter(isBlocked) : rows;
  const blockedCount = rows.filter(isBlocked).length;

  return (
    <div className="space-y-4">
      <PageHeader
        title={t('supplierOrders.title')}
        description={<Trans i18nKey="supplierOrders.intro" components={{ 1: <strong /> }} />}
      />

      {/* Feedback 3.12 (CRITICAL) — the lines the automatic sourcing could not place: nothing is
          ordered for them, whatever the order screen used to say. The buyer raises the supplier
          order by hand, or cancels the line and tells the customer. Never hidden by a filter. */}
      {unsourced.length > 0 && (
        <section className="rounded-lg border border-destructive/50 bg-destructive/5 p-4">
          <h2 className="font-semibold text-destructive">
            {t('supplierOrders.unsourced.title', { count: unsourced.length })}
          </h2>
          <p className="mb-3 text-xs text-muted-foreground">{t('supplierOrders.unsourced.hint')}</p>
          <table className="w-full text-sm">
            <thead className="text-muted-foreground">
              <tr className="border-b">
                <th className="py-1 text-left">{t('supplierOrders.unsourced.order')}</th>
                <th className="text-left">{t('supplierOrders.unsourced.customer')}</th>
                <th className="text-left">{t('supplierOrders.unsourced.part')}</th>
                <th className="text-center">{t('supplierOrders.unsourced.qty')}</th>
                <th className="text-left">{t('supplierOrders.unsourced.since')}</th>
                {canPlace && <th className="text-left">{t('supplierOrders.unsourced.raise')}</th>}
              </tr>
            </thead>
            <tbody>
              {unsourced.map((u) => (
                <tr key={u.lineId} className="border-b last:border-0">
                  <td className="py-1">
                    <Link
                      to={`/orders/${u.orderId}`}
                      className="font-mono underline-offset-2 hover:underline"
                    >
                      {u.orderNumber}
                    </Link>
                  </td>
                  <td>{u.customerName ?? '—'}</td>
                  <td>
                    <div className="truncate max-w-[22rem]">{u.name ?? u.sku}</div>
                    <div className="text-xs text-muted-foreground">
                      {[u.sku, u.brand, u.manufacturerRef].filter(Boolean).join(' · ')}
                    </div>
                  </td>
                  <td className="text-center tabular-nums">{u.quantity}</td>
                  <td className="text-xs text-muted-foreground">{time(u.since)}</td>
                  {canPlace && (
                    <td className="py-1">
                      <div className="flex flex-wrap items-center gap-1">
                        <select
                          className="rounded border bg-background px-1 py-0.5 text-xs"
                          value={handPo[u.lineId]?.supplierId ?? suppliers[0]?.id ?? ''}
                          onChange={(e) =>
                            setHandPo((h) => ({
                              ...h,
                              [u.lineId]: {
                                supplierId: e.target.value,
                                cost: h[u.lineId]?.cost ?? String(u.purchasePriceHT ?? ''),
                              },
                            }))
                          }
                        >
                          {suppliers.map((s) => (
                            <option key={s.id} value={s.id}>
                              {s.code}
                              {s.apiEnabled ? '' : ` · ${t('supplierOrders.offApi')}`}
                            </option>
                          ))}
                        </select>
                        <input
                          className="w-20 rounded border bg-background px-1 py-0.5 text-right text-xs tabular-nums"
                          inputMode="decimal"
                          aria-label={t('supplierOrders.unsourced.cost')}
                          placeholder={t('supplierOrders.unsourced.cost')}
                          value={handPo[u.lineId]?.cost ?? String(u.purchasePriceHT ?? '')}
                          onChange={(e) =>
                            setHandPo((h) => ({
                              ...h,
                              [u.lineId]: {
                                supplierId: h[u.lineId]?.supplierId ?? suppliers[0]?.id ?? '',
                                cost: e.target.value,
                              },
                            }))
                          }
                        />
                        <button
                          type="button"
                          className="rounded bg-primary px-2 py-0.5 text-xs text-primary-foreground disabled:opacity-60"
                          disabled={busy === u.lineId || suppliers.length === 0}
                          onClick={() => void raiseByHand(u)}
                        >
                          {t('supplierOrders.unsourced.raise')}
                        </button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}

      <section className="flex flex-wrap items-center gap-3 text-sm">
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            className={checkboxClass}
            checked={all}
            onChange={(e) => setAll(e.target.checked)}
          />
          {t('supplierOrders.allHistory')}
        </label>
        <label className="flex items-center gap-2">
          <input
            className={checkboxClass}
            type="checkbox"
            checked={onlyBlocked}
            onChange={(e) => setOnlyBlocked(e.target.checked)}
          />
          {t('supplierOrders.onlyBlocked')}
          {blockedCount > 0 && (
            <span className="rounded-full bg-destructive/15 text-destructive px-2 text-xs">
              {blockedCount}
            </span>
          )}
        </label>
        <button onClick={() => void load()} className="border rounded px-3 py-1">
          {t('supplierOrders.refresh')}
        </button>
      </section>

      <section className="border rounded bg-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-muted-foreground">
            <tr className="border-b">
              <th className="text-left p-2">{t('supplierOrders.columns.order')}</th>
              <th className="text-left p-2">{t('supplierOrders.columns.supplier')}</th>
              <th className="text-left p-2">{t('supplierOrders.columns.status')}</th>
              <th className="text-right p-2">{t('supplierOrders.columns.totalHt')}</th>
              <th className="text-center p-2">{t('supplierOrders.columns.received')}</th>
              <th className="text-left p-2">{t('supplierOrders.columns.origin')}</th>
              <th className="p-2" />
            </tr>
          </thead>
          <tbody>
            {visible.map((p) => {
              const state = sendState(p);
              return (
                <tr key={p.id} className="border-b last:border-0 align-top">
                  <td className="p-2">
                    <button
                      className="font-mono underline-offset-2 hover:underline"
                      onClick={() => void openDetail(p.id)}
                    >
                      {p.poNumber}
                    </button>
                    <div className="text-xs text-muted-foreground">{time(p.createdAt)}</div>
                    {p.supplierOrderRef && (
                      <div className="text-xs text-muted-foreground">
                        {t('supplierOrders.ref', { ref: p.supplierOrderRef })}
                      </div>
                    )}
                  </td>
                  <td className="p-2">
                    {p.supplierName}
                    {!p.supplierApiEnabled && (
                      <div className="text-xs text-muted-foreground">
                        {t('supplierOrders.offApi')}
                      </div>
                    )}
                  </td>
                  <td className="p-2">
                    <span
                      className={`rounded px-2 py-0.5 text-xs ${STATUS_TONE[p.status] ?? 'bg-muted'}`}
                    >
                      {statusLabel(p.status)}
                    </span>
                    {state && <div className={`text-xs mt-1 ${state.tone}`}>{state.label}</div>}
                    {p.closedShort && (
                      <div className="text-xs text-warning mt-1">
                        {t('supplierOrders.closedShort')}
                      </div>
                    )}
                    {p.brfNumber && (
                      <div className="text-xs text-muted-foreground mt-1">
                        {t('supplierOrders.brf', { number: p.brfNumber })}
                      </div>
                    )}
                  </td>
                  <td className="p-2 text-right tabular-nums">{money(p.totalHT)}</td>
                  <td className="p-2 text-center tabular-nums">
                    {p.qtyReceived}/{p.qtyOrdered}
                  </td>
                  <td className="p-2 text-xs">
                    {p.autoCreated ? (
                      <>
                        {t('supplierOrders.autoCustomer')}{' '}
                        {p.orderNumber && <span className="font-mono">{p.orderNumber}</span>}
                      </>
                    ) : (
                      t('supplierOrders.replenishment')
                    )}
                  </td>
                  <td className="p-2">
                    {(canPlace || canApprove || canCancel) && (
                      <div className="flex flex-wrap justify-end gap-1">
                        {p.status === 'PENDING' &&
                          canPlace &&
                          (isUncertain(p) ? (
                            // The last send may have landed: never a blind "Retry". The buyer
                            // looks for the PO number on the supplier portal first.
                            <>
                              <button
                                onClick={() => void markPlaced(p)}
                                disabled={busy === p.id}
                                className="bg-primary text-primary-foreground rounded px-2 py-1 text-xs disabled:opacity-40"
                              >
                                {t('supplierOrders.foundOnPortal')}
                              </button>
                              <button
                                onClick={() => void send(p, true)}
                                disabled={busy === p.id}
                                className="border border-primary text-primary rounded px-2 py-1 text-xs disabled:opacity-40"
                              >
                                {t('supplierOrders.notOnPortalResend')}
                              </button>
                            </>
                          ) : p.lastSendError === 'MANUAL_CHANNEL' ? (
                            // Off-API supplier: /send would only re-park the row, so offer the
                            // action that actually resolves it.
                            <button
                              onClick={() => void markPlaced(p)}
                              disabled={busy === p.id}
                              className="bg-primary text-primary-foreground rounded px-2 py-1 text-xs disabled:opacity-40"
                            >
                              {t('supplierOrders.markPlaced')}
                            </button>
                          ) : (
                            <button
                              onClick={() => void send(p)}
                              disabled={busy === p.id}
                              className="bg-primary text-primary-foreground rounded px-2 py-1 text-xs disabled:opacity-40"
                            >
                              {p.sendAttempts > 0
                                ? t('supplierOrders.resend')
                                : t('supplierOrders.send')}
                            </button>
                          ))}
                        {/* Held above the threshold. Approving clears it; the Transmit button
                            appears on the next refresh, once the PO is PENDING (E5). A row that
                            is ALSO parked uncertain is not approved away — it is resolved on the
                            supplier's portal first. */}
                        {p.status === 'PENDING_APPROVAL' &&
                          (isUncertain(p)
                            ? canPlace && (
                                <button
                                  onClick={() => void markPlaced(p)}
                                  disabled={busy === p.id}
                                  className="bg-primary text-primary-foreground rounded px-2 py-1 text-xs disabled:opacity-40"
                                >
                                  {t('supplierOrders.foundOnPortal')}
                                </button>
                              )
                            : canApprove && (
                                <button
                                  onClick={() => void approve(p)}
                                  disabled={busy === p.id}
                                  className="bg-success text-primary-foreground rounded px-2 py-1 text-xs disabled:opacity-40"
                                >
                                  {t('supplierOrders.approve')}
                                </button>
                              ))}
                        {canCancel &&
                          ['PENDING', 'PENDING_APPROVAL', 'CONFIRMED', 'SHIPPED'].includes(
                            p.status
                          ) && (
                            <button
                              onClick={() => void cancel(p)}
                              disabled={busy === p.id}
                              className="border border-destructive text-destructive rounded px-2 py-1 text-xs disabled:opacity-40"
                            >
                              {t('supplierOrders.cancel')}
                            </button>
                          )}
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
            {visible.length === 0 && (
              <tr>
                <td colSpan={7} className="p-4 text-center text-muted-foreground">
                  {t('supplierOrders.empty')}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </section>

      {/* Feedback 3.11 (2026-10-27) — the order's lines open in a dialog over the list, not in a
          panel at the foot of the page the user had to scroll to. Escape or the backdrop closes
          it; the list and its filters stay where they were. */}
      {detail && (
        <Modal
          open
          wide
          onClose={() => setDetail(null)}
          title={`${detail.poNumber} — ${detail.supplierName}${
            detail.supplierOrderRef
              ? ` · ${t('supplierOrders.ref', { ref: detail.supplierOrderRef })}`
              : ''
          }`}
          subtitle={
            <>
              {detail.orderNumber && (
                <span>
                  {t('supplierOrders.customerOrder')}{' '}
                  <span className="font-mono">{detail.orderNumber}</span>
                </span>
              )}
              {sendState(detail) && (
                <p className={`mt-1 ${sendState(detail)!.tone}`}>
                  ⚠ {sendState(detail)!.label}
                  {sendState(detail)!.detail && ` — ${sendState(detail)!.detail}`}
                </p>
              )}
            </>
          }
          footer={<p className="text-xs text-muted-foreground">{t('supplierOrders.footnote')}</p>}
        >
          <table className="w-full text-sm">
            <thead className="text-muted-foreground">
              <tr className="border-b">
                <th className="text-left py-1">{t('supplierOrders.lines.item')}</th>
                <th className="text-center">{t('supplierOrders.lines.ordered')}</th>
                <th className="text-center">{t('supplierOrders.lines.ofWhichCustomer')}</th>
                <th className="text-center">{t('supplierOrders.lines.received')}</th>
                <th className="text-center">{t('supplierOrders.lines.short')}</th>
              </tr>
            </thead>
            <tbody>
              {detail.lines.map((l) => (
                <tr key={l.id} className="border-b last:border-0">
                  <td className="py-1">
                    <div className="truncate max-w-[22rem]">{l.productName ?? l.productId}</div>
                    <div className="text-xs text-muted-foreground flex flex-wrap gap-x-2">
                      {l.productSku && <span className="font-mono">{l.productSku}</span>}
                      {l.productBrand && <span>{l.productBrand}</span>}
                      {l.customerReleased && (
                        <span className="text-warning">
                          {t('supplierOrders.lines.customerReleased')}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="text-center tabular-nums">{l.quantity}</td>
                  <td className="text-center tabular-nums">
                    {l.quantityCustomer ?? '—'}
                    {l.quantityCustomer != null && l.quantityCustomer < l.quantity && (
                      <div className="text-xs text-muted-foreground">
                        {t('supplierOrders.lines.surplus', {
                          count: l.quantity - l.quantityCustomer,
                        })}
                      </div>
                    )}
                  </td>
                  <td className="text-center tabular-nums">{l.quantityReceived}</td>
                  <td className="text-center tabular-nums">
                    {l.quantityShort > 0 ? (
                      <span className="text-warning">{l.quantityShort}</span>
                    ) : (
                      '—'
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Modal>
      )}
    </div>
  );
}
